import Swal from 'sweetalert2'
import {
  getVoucherById,
  resendVoucher,
  updateVoucherClientEmail,
} from '../../../coreGlobal/_resquest'
import {IVoucherResponse} from '../../../coreGlobal/models/voucher'
import {formatDate} from '../../../utils/dayJs/format'
import {useEffect, useState} from 'react'
import {formatMonetaryToBr} from '../../../utils/formatMonetaryToBr'
import {Link, useParams} from 'react-router-dom'
import useAuthorization from '../../../hooks/useAuthorization'
import {TooltipWrapper} from '../../../components/Tooltip'

const ListVoucher = () => {
  let {id} = useParams()
  const [vouchers, setVouchers] = useState<IVoucherResponse[]>([])
  const [loading, setLoading] = useState(false)

  const isAuthorizedToResend = useAuthorization('vouchers.resend')
  const isAuthorizedToUpdateClientEmail = useAuthorization('vouchers.update_client_email')

  const checkVoucher = async () => {
    setLoading(true)
    try {
      const data = await getVoucherById(String(id))
      setVouchers(data)
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response.data.message,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
    setLoading(false)
  }

  useEffect(() => {
    checkVoucher()
  }, [])

  const handleResend = async (voucher: IVoucherResponse) => {
    const result = await Swal.fire({
      title: 'Reenviar voucher?',
      text: `O voucher será reenviado para ${voucher.clientEmail}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Reenviar',
      cancelButtonText: 'Cancelar',
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      await resendVoucher(voucher.id)
      await Swal.fire({
        icon: 'success',
        title: 'Voucher reenviado com sucesso!',
        showConfirmButton: true,
      })
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response.data.message,
        showConfirmButton: true,
      })
    }
  }

  const isVoucherLocked = (voucher: IVoucherResponse) => {
    const isUsed = !!voucher.usedAt
    const isExpired = !!voucher.expiresAt && new Date(voucher.expiresAt) < new Date()
    return isUsed || isExpired
  }

  const getFirstAndLastName = (fullName: string) => {
    const names = fullName.trim().split(/\s+/)
    return names.length > 1 ? `${names[0]} ${names[names.length - 1]}` : names[0]
  }

  const handleUpdateClientEmail = async (voucher: IVoucherResponse) => {
    const result = await Swal.fire({
      title: 'Alterar e-mail do cliente',
      input: 'email',
      inputLabel: 'Novo e-mail',
      inputValue: voucher.clientEmail || '',
      inputPlaceholder: 'Informe o novo e-mail...',
      showCancelButton: true,
      confirmButtonText: 'Salvar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value) {
          return 'Informe um e-mail.'
        }
        return undefined
      },
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      await updateVoucherClientEmail(voucher.id, result.value)
      await Swal.fire({
        icon: 'success',
        title: 'E-mail atualizado com sucesso!',
        showConfirmButton: true,
      })
      await checkVoucher()
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response.data.message,
        showConfirmButton: true,
      })
    }
  }

  return (
    <div className='card-body py-3'>
      <div className='table-responsive'>
        <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
          <thead>
            <tr className='fw-bolder fs-6 text-gray-800'>
              <th>Franquia</th>
              <th>Venda</th>
              <th>Tipo</th>
              <th>Email do Cliente</th>
              <th>Valor</th>
              <th>Criado em</th>
              <th>Expira em</th>
              <th>Usado em</th>
              <th>Usado Cobrança</th>
              <th>Usado Cliente</th>
              {(isAuthorizedToResend || isAuthorizedToUpdateClientEmail) && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {vouchers?.map((voucher, index) => {
              return (
                <tr key={`listVoucherTr${index}`}>
                  <td>{voucher?.franchise?.name ? voucher.franchise.name : ''}</td>
                  <td>
                    {voucher?.distract?.saleId ? (
                      <Link to={`/sales/list/${voucher.distract.saleId}`}>
                        {voucher.distract.saleId}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {voucher?.typeName ? (
                      <span
                        className={`badge ${
                          voucher.type === 'annulment'
                            ? 'badge-light-danger'
                            : 'badge-light-warning'
                        }`}
                      >
                        {voucher.typeName}
                      </span>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>{voucher?.clientEmail ? voucher.clientEmail : ''}</td>
                  <td>{voucher?.amount ? formatMonetaryToBr(voucher.amount) : ''}</td>
                  <td>{voucher?.createdAt ? formatDate(voucher.createdAt) : ''}</td>
                  <td>{voucher?.expiresAt ? formatDate(voucher?.expiresAt) : ''}</td>
                  <td>{voucher?.usedAt ? formatDate(voucher?.usedAt) : 'Não'}</td>
                  <td>
                    {voucher?.billingId ? (
                      <span className='badge badge-light-success'>
                        Cobrança #{voucher.billingId}
                      </span>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {voucher?.redeemedClient
                      ? `#${voucher.redeemedClient.id} - ${getFirstAndLastName(
                          voucher.redeemedClient.name
                        )}`
                      : ''}
                  </td>
                  {(isAuthorizedToResend || isAuthorizedToUpdateClientEmail) && (
                    <td>
                      {!isVoucherLocked(voucher) && (
                        <>
                          {isAuthorizedToResend && (
                            <TooltipWrapper tooltipText='Reenviar voucher' tooltipPlacement='top'>
                              <button
                                type='button'
                                className='btn btn-icon btn-sm btn-light-primary me-2'
                                onClick={() => handleResend(voucher)}
                              >
                                <i className='ki-duotone ki-send fs-3'>
                                  <span className='path1'></span>
                                  <span className='path2'></span>
                                </i>
                              </button>
                            </TooltipWrapper>
                          )}
                          {isAuthorizedToUpdateClientEmail && (
                            <TooltipWrapper
                              tooltipText='Alterar e-mail do cliente'
                              tooltipPlacement='top'
                            >
                              <button
                                type='button'
                                className='btn btn-icon btn-sm btn-light-warning'
                                onClick={() => handleUpdateClientEmail(voucher)}
                              >
                                <i className='ki-duotone ki-sms fs-3'>
                                  <span className='path1'></span>
                                  <span className='path2'></span>
                                </i>
                              </button>
                            </TooltipWrapper>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export {ListVoucher}
